'use client'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format, InputMask } from '@react-input/mask'
import React, { useLayoutEffect, useRef } from 'react'
import { Controller } from 'react-hook-form'
import type { IMask } from '@/constants/masks'
import FormError from '../FormError'
import { inputClass as fieldClass } from '../Input/classes'
import Label from '../Label'

export interface IMaskedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'type'> {
    name: string
    /**
     * Sempre controlado (`Controller`): o valor que vem da API sem máscara (ex.: telefone "41999998888") só aparece
     * formatado se o componente formatar o valor do formulário a cada render, o que o `register` não permite.
     */
    control: any
    /** Máscara de `src/constants/masks.ts`. Sem máscara, é um campo de texto comum. */
    mask?: IMask
    labelText?: string
    /** Botão de envio dentro do campo (ex.: o "OK" do CEP no carrinho), com texto ou ícone. */
    displayNestedButton?: boolean
    nestedButtonText?: string
    nestedButtonIcon?: IconDefinition
    isRequired?: boolean
    inputClass?: string
    type?: string
}

/**
 * Campo com máscara (`@react-input/mask`) no visual dos campos da loja. O valor vindo da API sem máscara (ex.: telefone
 * "41999998888") aparece formatado; o formulário recebe o valor formatado, como antes.
 */
const MaskedInput: React.FC<IMaskedInputProps> = ({
    name,
    control,
    mask,
    labelText,
    isRequired,
    id,
    className,
    inputClass = '',
    placeholder,
    maxLength,
    onChange,
    onBlur,
    readOnly = false,
    defaultValue,
    displayNestedButton = false,
    nestedButtonText = '',
    nestedButtonIcon,
}) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)

    // O @react-input/mask acompanha a seleção do campo focado com um setTimeout em laço que só para no blur, e o
    // unregister ao desligar o ref não cancela o timer. O blur antes de desmontar encerra o laço: a limpeza do
    // useLayoutEffect deste componente roda antes de o ref do input (filho) ser desligado.
    useLayoutEffect(() => {
        return () => {
            const input = wrapperRef.current?.querySelector('input')
            if (input && document.activeElement === input) input.blur()
        }
    }, [])

    const display = (value: unknown) => {
        const text = value == null ? '' : String(value)
        return mask && text ? format(text, mask) : text
    }

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field, fieldState }) => {
                const error = fieldState.error?.message
                const inputId = id || name
                const errorId = `${inputId}-error`
                const inputProps = {
                    ...field,
                    value: display(field.value),
                    className: `${readOnly ? 'cursor-not-allowed!' : ''} ${inputClass} ${fieldClass(Boolean(error))}`,
                    placeholder,
                    maxLength,
                    id: inputId,
                    type: 'text',
                    'aria-invalid': error ? true : undefined,
                    'aria-describedby': error ? errorId : undefined,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(e)
                        onChange?.(e)
                    },
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                        field.onBlur()
                        onBlur?.(e)
                    },
                    disabled: readOnly,
                }

                return (
                    <div ref={wrapperRef} className={`form-group relative ${className ?? ''}`}>
                        <Label
                            htmlFor={inputId}
                            labelText={labelText}
                            isRequired={isRequired}
                            hasError={Boolean(error)}
                            className={className}
                        >
                            <div className="w-full relative flex flex-row items-center justify-between gap-0">
                                {mask ? (
                                    <InputMask mask={mask.mask} replacement={mask.replacement} {...inputProps} />
                                ) : (
                                    <input {...inputProps} />
                                )}

                                {displayNestedButton && nestedButtonIcon && (
                                    <button type="submit" className="absolute top-0 right-0 z-20 w-10 h-10">
                                        <FontAwesomeIcon icon={nestedButtonIcon} />
                                    </button>
                                )}

                                {displayNestedButton && nestedButtonText && (
                                    <button
                                        type="submit"
                                        className="w-10 h-10 opacity-80 transition-opacity hover:opacity-100"
                                    >
                                        <span className="text-xl font-bold text-destaques">{nestedButtonText}</span>
                                    </button>
                                )}
                            </div>
                            {error && <FormError id={errorId}>{error}</FormError>}
                        </Label>
                    </div>
                )
            }}
        />
    )
}

export default MaskedInput
